import { forwardRef, useMultiStyleConfig } from '@chakra-ui/react-v2';
import { cx } from '@chakra-ui/utils';

import {
  ChildNodeFileIcon,
  PdfFileIcon,
  Icon,
  useFileContext,
  type IconProps,
  FileIcon,
  ImageFileIcon,
  VideoFileIcon,
  SpreadsheetFileIcon,
  TextFileIcon,
  ZipFileIcon,
} from '@frontend/legacy-components';

export type FileInlineCardIconProps = IconProps;

/**
 * FileInlineCardIcon component that automatically selects the appropriate icon
 * based on the file type from the FileContext.
 *
 * - Assembly child files (with assemblyParentFilename) always use ChildNodeFileIcon
 * - Regular files use icons from FILE_TYPE_ICON_MAP based on their fileType
 * - Falls back to FileIcon for unknown file types
 */
export const FileInlineCardIcon = forwardRef<FileInlineCardIconProps, 'svg'>(
  ({ className, ...restProps }, ref) => {
    const { assemblyParentFilename, fileType } = useFileContext();
    const styles = useMultiStyleConfig('FileInlineCard');

    /**
     * Gets the appropriate icon component for a given file type
     */
    const getIconForFileType = (fileType: string | null | undefined) => {
      // Mapping of file types to their corresponding icons
      const FILE_TYPE_ICON_MAP = {
        // Document types
        pdf: PdfFileIcon,
        tiff: PdfFileIcon,
        tif: PdfFileIcon,
        doc: FileIcon,
        docx: FileIcon,
        txt: TextFileIcon,
        rtf: TextFileIcon,

        // Image types
        jpg: ImageFileIcon,
        jpeg: ImageFileIcon,
        png: ImageFileIcon,
        gif: ImageFileIcon,
        bmp: ImageFileIcon,
        svg: ImageFileIcon,
        webp: ImageFileIcon,

        // Video types
        mp4: VideoFileIcon,
        avi: VideoFileIcon,
        mov: VideoFileIcon,
        wmv: VideoFileIcon,
        flv: VideoFileIcon,
        webm: VideoFileIcon,
        mkv: VideoFileIcon,

        // Spreadsheet types
        xls: SpreadsheetFileIcon,
        xlsx: SpreadsheetFileIcon,
        csv: SpreadsheetFileIcon,

        // Archive types
        zip: ZipFileIcon,
        rar: ZipFileIcon,
        tar: ZipFileIcon,
        gz: ZipFileIcon,
        '7z': ZipFileIcon,
        '7zip': ZipFileIcon,

        // CAD types (using generic file icon for now)
        dwg: FileIcon,
        dxf: FileIcon,
        step: FileIcon,
        stp: FileIcon,
        iges: FileIcon,
        igs: FileIcon,

        // Default fallback
        default: FileIcon,
      };

      if (!fileType) {
        return FILE_TYPE_ICON_MAP.default;
      }

      const normalizedFileType = fileType.toLowerCase();
      const iconComponent =
        FILE_TYPE_ICON_MAP[
          normalizedFileType as keyof typeof FILE_TYPE_ICON_MAP
        ] || FILE_TYPE_ICON_MAP.default;
      return iconComponent;
    };

    // For assembly child files, always use ChildNodeFileIcon
    if (assemblyParentFilename) {
      return (
        <Icon
          ref={ref}
          className={cx('file-inline-card__icon', className)}
          __css={styles.icon}
          as={ChildNodeFileIcon}
          {...restProps}
        />
      );
    }

    // For regular files, use the appropriate icon based on file type
    const IconComponent = getIconForFileType(fileType);

    return (
      <Icon
        ref={ref}
        className={cx('file-inline-card__icon', className)}
        __css={styles.icon}
        as={IconComponent}
        {...restProps}
      />
    );
  },
);
